import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { LogEntry, LogsResponse, ProvidersResponse, SortStep } from '../../api/types';
import { usePrefsStore } from '../../stores/prefs';

// Stable defaults so Zustand selectors return the same reference across renders
// (a fresh `?? {}` would cause an infinite re-render — React error #185).
const DEFAULT_SORT: SortStep[] = [{ key: 'timestampUtc', dir: 'desc' }];

export function useLogs() {
  // prefsLoaded gates everything that fetches: until the persisted prefs come
  // back from /api/users/me/preferences, `source` / `window_` / etc. below all
  // return defaults. If we fired a load with those defaults and then a second
  // load with the persisted values right after, the second response can race
  // with the first (or simply return different data) and the table flickers
  // populated → empty.
  const prefsLoaded = usePrefsStore((s) => s.loaded);

  // Select only the primitives we need. Each is stable (string/bool) so no equality drama.
  const source = usePrefsStore((s) => s.prefs.logViewer?.source ?? 'all');
  const window_ = usePrefsStore((s) => s.prefs.logViewer?.window ?? '15m');
  const severity = usePrefsStore((s) => s.prefs.logViewer?.severity ?? '');
  const limit = usePrefsStore((s) => s.prefs.logViewer?.limit ?? '200');
  const autoRefresh = usePrefsStore((s) => s.prefs.logViewer?.autoRefresh ?? false);
  // sortChain is an array — only return the persisted reference, never a new default literal.
  const persistedSortChain = usePrefsStore((s) => s.prefs.logViewer?.sortChain);
  const sortChain = useMemo(() => persistedSortChain ?? DEFAULT_SORT, [persistedSortChain]);
  const updateLogViewer = usePrefsStore((s) => s.updateLogViewer);

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [count, setCount] = useState(0);
  const [windowMinutes, setWindowMinutes] = useState(15);
  const [notes, setNotes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Monotonic id stamped on each in-flight fetch; only the latest is allowed to
  // commit results. Stops a slow response from clobbering a fresh one's data —
  // matters for the autoRefresh tick, manual Refresh + filter changes,
  // React Strict Mode's double-effect in dev, etc.
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    const myFetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ since: window_, limit, source });
      if (severity) params.set('severity', severity);
      const { data } = await api.get<LogsResponse>('/api/logs', { params });
      if (fetchIdRef.current !== myFetchId) return;  // a newer fetch has already started; discard
      setEntries(data.entries ?? []);
      setCount(data.count ?? 0);
      setWindowMinutes(data.windowMinutes ?? 15);
      setNotes(data.notes ?? []);
    } finally {
      if (fetchIdRef.current === myFetchId) setLoading(false);
    }
  }, [source, window_, severity, limit]);

  useEffect(() => {
    if (prefsLoaded) load();
  }, [prefsLoaded, load]);

  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (prefsLoaded && autoRefresh) timerRef.current = setInterval(load, 10000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [prefsLoaded, autoRefresh, load]);

  return {
    entries, count, windowMinutes, notes, loading, load,
    source, window: window_, severity, limit, autoRefresh, sortChain,
    setSource: (v: 'all' | 'insights' | 'file' | 'db') => updateLogViewer({ source: v }),
    setWindow: (v: string) => updateLogViewer({ window: v }),
    setSeverity: (v: '' | 'info' | 'warn' | 'error') => updateLogViewer({ severity: v }),
    setLimit: (v: string) => updateLogViewer({ limit: v }),
    setAutoRefresh: (v: boolean) => updateLogViewer({ autoRefresh: v }),
    setSortChain: (v: SortStep[]) => updateLogViewer({ sortChain: v }),
  };
}

export function useProviders() {
  const [providers, setProviders] = useState<ProvidersResponse['providers']>([]);
  const refresh = useCallback(async () => {
    const { data } = await api.get<ProvidersResponse>('/api/logs/providers');
    setProviders(data.providers ?? []);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const toggle = async (name: string, enabled: boolean) => {
    await api.post('/api/logs/providers', { Name: name, Enabled: enabled });
    setProviders((cur) => cur.map((p) => p.name === name ? { ...p, enabled } : p));
  };
  return { providers, toggle };
}
